import _extends from '@babel/runtime/helpers/extends';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import { autoUpdate, computePosition, flip, shift, limitShift, offset, arrow } from '@floating-ui/dom';
import classnames from 'classnames';
import React__default, { useRef, useState, useEffect, useCallback } from 'react';
import { newLocation } from '../utils/FloatUILocation.js';
import alertUserOfDeprecatedProps from '../utils/deprecation.js';

var _excluded = ["as", "className", "content", "children", "location", "tabIndex", "tooltip", "data-testid"];
var Tooltip = /*#__PURE__*/React__default.memo(function (props) {
  var _props$as = props.as,
    As = _props$as === void 0 ? 'span' : _props$as,
    className = props.className,
    content = props.content,
    children = props.children,
    _props$location = props.location,
    location = _props$location === void 0 ? 'none' : _props$location,
    _props$tabIndex = props.tabIndex,
    tabIndex = _props$tabIndex === void 0 ? '-1' : _props$tabIndex,
    tooltip = props.tooltip,
    dataTestId = props['data-testid'],
    otherProps = _objectWithoutProperties(props, _excluded);
  alertUserOfDeprecatedProps('Tooltip', otherProps);
  var arrowRef = useRef(null);
  var tooltipRef = useRef(null);
  var tooltipContentRef = useRef(null);
  var cleanupFunction = useRef(null);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    showTooltip = _useState2[0],
    setShowTooltip = _useState2[1];
  var _useState3 = useState(location),
    _useState4 = _slicedToArray(_useState3, 2),
    tooltipLocation = _useState4[0],
    setTooltipLocation = _useState4[1];
  useEffect(function () {
    if (showTooltip) {
      var _tooltipContentRef$cu;
      cleanupFunction.current && cleanupFunction.current();
      tooltipContentRef === null || tooltipContentRef === void 0 ? void 0 : (_tooltipContentRef$cu = tooltipContentRef.current) === null || _tooltipContentRef$cu === void 0 ? void 0 : _tooltipContentRef$cu.setAttribute('data-show', '');
      if (location !== 'none') {
        var arrowEl = arrowRef === null || arrowRef === void 0 ? void 0 : arrowRef.current;
        var arrowLen = arrowEl.offsetWidth;

        // Get half the arrow box's hypotenuse length
        var floatingOffset = Math.sqrt(2 * Math.pow(arrowLen, 2)) / 2;
        cleanupFunction.current = autoUpdate(tooltipRef.current, tooltipContentRef.current, function () {
          tooltipRef.current && tooltipContentRef.current && computePosition(tooltipRef.current, tooltipContentRef.current, {
            middleware: [flip(), shift({
              limiter: limitShift()
            }), offset(floatingOffset), arrow({
              element: arrowEl
            })],
            placement: newLocation(location)
          }).then(function (_ref) {
            var x = _ref.x,
              y = _ref.y,
              middlewareData = _ref.middlewareData,
              placement = _ref.placement;
            if (tooltipContentRef !== null && tooltipContentRef !== void 0 && tooltipContentRef.current) {
              Object.assign(tooltipContentRef.current.style, {
                left: "".concat(x, "px"),
                top: "".concat(y, "px")
              });
            }
            setTooltipLocation(placement);
            var side = placement.split('-')[0];
            var staticSide = {
              top: 'bottom',
              right: 'left',
              bottom: 'top',
              left: 'right'
            }[side];
            if (middlewareData.arrow) {
              var _middlewareData$arrow = middlewareData.arrow,
                _x = _middlewareData$arrow.x,
                _y = _middlewareData$arrow.y;
              //Format to default empty in the case of top-left, top-right, bottom-left, and bottom-right
              //this way the .scss folder can override the values otherwise these middleware values get
              //applied last
              var paddingOffset = newLocation(location).includes('start') || newLocation(location).includes('end');
              Object.assign(arrowEl.style, _defineProperty({
                left: paddingOffset ? '' : "".concat(_x, "px"),
                top: paddingOffset ? '' : "".concat(_y, "px"),
                right: '',
                bottom: ''
              }, staticSide, "".concat(-arrowLen / 2, "px")));
            }
          });
        });
      }
    } else {
      var _tooltipContentRef$cu2;
      tooltipContentRef === null || tooltipContentRef === void 0 ? void 0 : (_tooltipContentRef$cu2 = tooltipContentRef.current) === null || _tooltipContentRef$cu2 === void 0 ? void 0 : _tooltipContentRef$cu2.removeAttribute('data-show');

      // ensure events are removed when not showing tooltip
      cleanupFunction.current && cleanupFunction.current();
    }
  }, [showTooltip, location]);
  var show = useCallback(function () {
    setShowTooltip(true);
  }, []);
  var hide = useCallback(function () {
    setShowTooltip(false);
  }, []);
  var finalClasses = classnames('C-Tooltip', className);
  var tooltipContent = tooltip || content;
  if (location === 'none') {
    return /*#__PURE__*/React__default.createElement(As, _extends({
      className: finalClasses,
      "data-tooltip": tooltipContent,
      "data-testid": dataTestId,
      tabIndex: tabIndex
    }, otherProps), /*#__PURE__*/React__default.createElement("span", {
      role: "tooltip",
      className: "hc-sr-only"
    }, tooltipContent), children);
  }
  return /*#__PURE__*/React__default.createElement("span", _extends({
    ref: tooltipRef,
    className: finalClasses,
    "aria-describedby": "tooltip",
    "data-testid": dataTestId,
    tabIndex: tabIndex,
    onMouseEnter: show,
    onMouseLeave: hide,
    onFocus: show,
    onBlur: hide,
    onPointerLeave: hide,
    onPointerEnter: show
  }, otherProps), /*#__PURE__*/React__default.createElement("div", {
    role: "tooltip",
    className: "C-Tooltip__Content",
    "data-testid": dataTestId ? "".concat(dataTestId, "-tooltipContent") : null,
    ref: tooltipContentRef
  }, tooltipContent, /*#__PURE__*/React__default.createElement("div", {
    className: "C-Tooltip__Arrow",
    location: tooltipLocation,
    "data-testid": dataTestId ? "".concat(dataTestId, "-tooltipArrow") : null,
    ref: arrowRef
  })), children);
});
Tooltip.displayName = 'Tooltip';

export { Tooltip as default };
